<script lang="ts">
  // Toggle which amenities count toward the "Daily places" score. Grouped by
  // category; enabling something new triggers a lookup for every candidate
  // (App.svelte watches enabledAmenityKeys), disabling is instant.
  import { enabledAmenityKeys, setAmenityEnabled, resetAmenityPrefs } from '../lib/state';
  import { AMENITIES, AMENITY_GROUP_LABELS, DEFAULT_AMENITY_KEYS } from '../lib/scoring';
  import type { AmenityGroup } from '../lib/types';

  const GROUPS: AmenityGroup[] = ['daily', 'parks', 'recreation'];
  const HINTS: Partial<Record<AmenityGroup, string>> = {
    parks: 'Sized from map data: pocket (any), neighborhood (5+ acres), big (50+ acres).',
    recreation: 'Courts, fields, pools and gyms from OpenStreetMap. Turn on what you actually use.',
  };

  $: isDefault = $enabledAmenityKeys.size === DEFAULT_AMENITY_KEYS.length
    && DEFAULT_AMENITY_KEYS.every((k) => $enabledAmenityKeys.has(k));
</script>

<div class="picker">
  {#each GROUPS as g}
    <div class="picker-group">
      <div class="picker-group-title">{AMENITY_GROUP_LABELS[g]}</div>
      <div class="picker-chips" role="group" aria-label={AMENITY_GROUP_LABELS[g]}>
        {#each AMENITIES.filter((a) => a.group === g) as a (a.key)}
          {@const on = $enabledAmenityKeys.has(a.key)}
          <button
            type="button"
            class="picker-chip"
            class:on
            aria-pressed={on}
            on:click={() => setAmenityEnabled(a.key, !on)}
          >{a.label}</button>
        {/each}
      </div>
      {#if HINTS[g]}<div class="picker-hint">{HINTS[g]}</div>{/if}
    </div>
  {/each}
  <div class="picker-foot">
    {#if $enabledAmenityKeys.size === 0}
      <span class="picker-warn">Nothing selected: places are left out of NestScore.</span>
    {:else}
      <span>{$enabledAmenityKeys.size} selected</span>
    {/if}
    {#if !isDefault}
      <button type="button" class="link-btn" on:click={resetAmenityPrefs}>Reset to defaults</button>
    {/if}
  </div>
</div>

<style>
  .picker { display: flex; flex-direction: column; gap: 12px; margin: 8px 0 4px; }
  .picker-group-title {
    font-size: 11px; font-weight: 600; color: var(--ink-soft); margin-bottom: 6px;
  }
  .picker-chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .picker-chip {
    appearance: none; cursor: pointer;
    padding: 5px 10px; border-radius: 999px;
    background: #fdfaf0; border: 1px solid var(--line); color: var(--ink);
    font-size: 12.5px; line-height: 1.2; font-family: inherit;
    transition: background 0.12s, border-color 0.12s, color 0.12s;
  }
  .picker-chip:hover { border-color: var(--accent); }
  .picker-chip.on {
    background: var(--accent); border-color: var(--accent); color: #fff;
  }
  .picker-chip:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
  .picker-hint { font-size: 11px; color: var(--ink-soft); margin-top: 6px; line-height: 1.4; }
  .picker-foot {
    display: flex; justify-content: space-between; align-items: center; gap: 8px;
    font-size: 11px; color: var(--ink-soft);
  }
  .picker-warn { color: var(--warn); }
  .link-btn {
    appearance: none; background: none; border: none; padding: 0; cursor: pointer;
    font: inherit; font-size: 11px; color: var(--accent); text-decoration: underline;
  }
</style>
